import { Box, Flex } from '@chakra-ui/react';
import { useForm, Form, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { useEffect } from 'react';

import { CheckCircleIcon, PencilOutlineIcon } from 'Asset/icon';
import { Condition } from 'Component/others';
import { useDebounce } from 'Helper/debounce';
import { TextBold, TextSemiBold, TextRegular } from 'UiKit/typography';
import InputComponent from 'UiKit/input';
import InputSelectComponent from 'UiKit/input-select';
import ButtonOutline from 'UiKit/button/buttonOutline';
import ButtonSolid from 'UiKit/button/buttonSolid';
import InputDatepicker from 'UiKit/input-datepicker';
import Badge from 'UiKit/badge';
import usePostUserAvailability from 'Domain/user/usePostUserAvailability';
import useGetAllRoles from 'Domain/role/useGetAllRoles';
import usePatchUser from 'Domain/user/usePatchUser';
import useGetBrand from 'Domain/brand/useGetBrand';
import useGetEcommerce from 'Domain/ecommerce/useGetEcommerce';
import usePostUser from 'Domain/user/usePostUser';
import useGetUserDetail from 'Domain/user/useGetUserDetail';

import { validatePassword, checkPasswordCriteria } from 'View/user/helper/password';
import checkInlineValidation from 'View/user/helper/checkInlineValidation';
import useHandleCancel from 'View/user/hooks/useHandleCancel';
import { getMenuPermissions } from 'Helper/getMenuPermissions';

import { defaultValueForm } from './const';
import styles from './styles.module.css';

const FormUpsert = () => {
  const { t } = useTranslation('user');
  const { control, formState, register, watch, handleSubmit, reset, setError, clearErrors, setValue, trigger } = useForm({
    defaultValues: defaultValueForm,
    mode: 'onChange',
  });

  const accessPermission = getMenuPermissions('master-data', 'user');

  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const { data: optionsRole } = useGetAllRoles();
  const { data: optionsBrand } = useGetBrand();
  const { data: optionsEcommerce } = useGetEcommerce({ enabled: true });
  const { data: selectedUser, isLoading: isLoadingUserDetail } = useGetUserDetail(id);
  const { mutate: postUser } = usePostUser();
  const { mutate: patchUser } = usePatchUser();

  const { mutate: checkUserAvailablity, isPending: isLoadingInlineVerification } = usePostUserAvailability(setError, clearErrors);

  const { handleClickCancel } = useHandleCancel();

  const userValidationHandler = useDebounce(checkUserAvailablity, 500);

  const { errors, isSubmitting } = formState;
  const formField = watch();
  const isExistingUserHost = formField?.role?.isHost;

  const isUpsert = searchParams.get('viewType') === 'upsert';
  const isReadOnly = searchParams.get('viewType') === 'read-only';
  const isEditForm = !isEmpty(id) && !isReadOnly;
  const isCreateForm = isEmpty(id);
  const formTitle = isEditForm ? t('formTitleEdit') : isReadOnly ? t('formTitleDetail') : t('formTitleCreate');

  const onSubmit = () => postUser(formField);
  const onSubmitUpdate = () => patchUser(formField);

  const onChangeInlineField = (event) => {
    const keyName = event?.target?.name;
    const value = event?.target?.value;

    clearErrors(keyName);

    const isInlineValidation = checkInlineValidation(keyName, value, selectedUser);

    if (isInlineValidation) userValidationHandler({ [keyName]: value });

    setValue(keyName, value);
  };

  useEffect(() => {
    if (!isCreateForm) reset(selectedUser);
  }, [isCreateForm, selectedUser, reset]);

  useEffect(() => {
    if (isEmpty(accessPermission)) navigate('/forbidden');
  }, [accessPermission]); // eslint-disable-line

  const isUserActive = !isCreateForm && selectedUser?.isActive;

  return (
    <Box className={styles['form-upsert__container']}>
      <Box className={styles['form-upsert__header']}>
        <Flex gap={'12px'}>
          <TextBold className={styles['form-upsert__title']}>{formTitle}</TextBold>
          <Condition statement={!isCreateForm}>
            <Badge variant={isUserActive ? 'success' : 'error'} children={isUserActive ? t('statusActive') : t('statusInactive')} />
          </Condition>
        </Flex>

        {/* View mode */}
        <Condition statement={isReadOnly}>
          <Box className={styles['form-upsert__header-action--readonly']}>
            <ButtonOutline className={styles['form-user__button--back']} onClick={() => navigate('/master-data/user')} type="button">
              <TextSemiBold>{t('formBackButton')}</TextSemiBold>
            </ButtonOutline>

            <Condition statement={accessPermission?.isCanEdit && isUserActive && !selectedUser?.role.isHost}>
              <ButtonOutline
                className={styles['form-user__button--edit']}
                onClick={() => navigate(`/master-data/user/upsert/${id}?viewType=upsert`)}
                type="button"
              >
                <PencilOutlineIcon size={16} />
                <TextSemiBold>{t('formEditButton')}</TextSemiBold>
              </ButtonOutline>
            </Condition>
          </Box>
        </Condition>

        {/* Edit mode */}
        <Condition statement={isEditForm}>
          <Box className={styles['form-upsert__header-action--edit']}>
            <ButtonOutline className={styles['form-user__button--back']} onClick={handleClickCancel} type="button" disabled={isSubmitting}>
              <TextSemiBold>{t('formCancelButton')}</TextSemiBold>
            </ButtonOutline>

            <ButtonSolid
              className={styles['form-user__button--update']}
              type="submit"
              isLoading={isSubmitting || postUser.isLoading}
              onClick={onSubmitUpdate}
              disabled={isSubmitting || postUser.isLoading || !formState.isValid}
            >
              <TextSemiBold>{t('formSubmitButton')}</TextSemiBold>
            </ButtonSolid>
          </Box>
        </Condition>
      </Box>

      <Box className={styles['form-upsert__body']}>
        <Form control={control} className={styles['form-user__container']} onSubmit={handleSubmit(onSubmit)}>
          {/* Account Details Section */}
          <Box className={styles['form-upsert__section']}>
            <TextBold className={styles['form-upsert__section-title']}>{t('formAccountDetailsTitle')}</TextBold>

            <Box className={styles['form-upsert__fields']}>
              <Flex gap={'24px'} alignItems={'center'} width={'100%'}>
                <Flex flex={1} width={'100%'}>
                  <InputComponent
                    label={t('formNameLabel')}
                    placeholder={t('formNamePlaceholder')}
                    className={`${styles['form-user__input']} ${styles['form-user__input--half']}`}
                    value={formField.name}
                    error={errors.name}
                    isReadOnly={isReadOnly || isExistingUserHost}
                    isRequired={true}
                    isLoading={isLoadingInlineVerification}
                    isFetching={isLoadingUserDetail}
                    register={{
                      ...register('name', {
                        required: 'Name is required',
                      }),
                      onChange: (event) => {
                        const rawValue = event.target.value;
                        const cleanedValue = rawValue.replace(/[^A-Za-z\s]/g, '').replace(/\s{2,}/g, ' ');
                        if (rawValue !== cleanedValue) return;
                        onChangeInlineField(event);
                      },
                    }}
                  />
                </Flex>

                <Flex flex={1}>
                  <Controller
                    control={control}
                    name="role"
                    rules={{
                      required: 'Role is required',
                      validate: (value) => value !== null || 'Please select a role',
                    }}
                    render={({ field }) => (
                      <InputSelectComponent
                        label={t('formRoleLabel')}
                        placeholder={t('formRolePlaceholder')}
                        options={optionsRole?.filter((role) => !role.isHost) || []}
                        className={`${styles['form-user__input']} ${styles['form-user__input--half']}`}
                        field={{
                          ...field,
                          onChange: (e) => {
                            field.onChange(e);
                            setValue('brand', []);
                          },
                        }}
                        error={errors.role}
                        value={formField.role}
                        isReadOnly={isReadOnly || isExistingUserHost}
                        isFetching={isLoadingUserDetail}
                        isRequired={true}
                      />
                    )}
                  />
                </Flex>
              </Flex>

              <Flex gap={'24px'} alignItems={'center'} width={'100%'}>
                <Flex flex={1} width={'100%'}>
                  <InputComponent
                    label={t('formEmployeeIdLabel')}
                    placeholder={t('formEmployeeIdPlaceholder')}
                    className={`${styles['form-user__input']} ${styles['form-user__input--half']}`}
                    value={formField.nip}
                    error={errors.nip}
                    isReadOnly={isReadOnly || isExistingUserHost}
                    isRequired={true}
                    isFetching={isLoadingUserDetail}
                    register={{
                      ...register('nip', { required: 'NIP is required' }),
                      onChange: (event) => {
                        const cleanedValue = event.target.value.replace(/[^0-9]/g, '');
                        setValue('nip', cleanedValue);
                      },
                    }}
                  />
                </Flex>

                <Flex flex={1}>
                  <Controller
                    control={control}
                    name="brand"
                    rules={{ required: formField.role?.isHost ? 'Brand is required' : false }}
                    render={({ field }) => (
                      <InputSelectComponent
                        label={t('formBrandLabel')}
                        placeholder={t('formBrandPlaceholder')}
                        options={optionsBrand?.map((brand) => ({
                          value: brand.id,
                          label: brand.label,
                        }))}
                        className={`${styles['form-user__input']} ${styles['form-user__input--half']}`}
                        field={field}
                        error={errors.brand}
                        isMulti={!formField?.role?.isHost}
                        isDisabled={!formField.role}
                        isFetching={isLoadingUserDetail}
                        value={formField.brand}
                        isReadOnly={isReadOnly || isExistingUserHost}
                        isRequired={formField.role?.isHost}
                      />
                    )}
                  />
                </Flex>
              </Flex>

              <Flex gap={'24px'} alignItems={'center'} width={'100%'}>
                <Flex gap={'24px'} width={'100%'} flex={1}>
                  <Flex flex={1}>
                    <InputDatepicker
                      label={t('formJoinDateLabel')}
                      placeholder={t('formJoinDatePlaceholder')}
                      className={`${styles['form-user__input--half']}`}
                      value={formField.joinDate}
                      isDisabled={isReadOnly || isExistingUserHost}
                      onChange={(e) => setValue('joinDate', e)}
                    />
                  </Flex>
                  <Flex flex={1}>
                    <InputDatepicker
                      label={t('formTerminateDateLabel')}
                      placeholder={t('formTerminateDatePlaceholder')}
                      className={`${styles['form-user__input--half']}`}
                      isDisabled={isReadOnly || isExistingUserHost}
                      value={formField.terminateDate}
                      onChange={(e) => setValue('terminateDate', e)}
                    />
                  </Flex>
                </Flex>

                <Flex flex={1}>
                  <Controller
                    control={control}
                    name="ecommerces"
                    rules={{ required: formField.role?.isHost ? 'Platform is required' : false }}
                    render={({ field }) => (
                      <InputSelectComponent
                        label={t('formPlatformLabel')}
                        placeholder={t('formPlatformPlaceholder')}
                        options={optionsEcommerce || []}
                        className={`${styles['form-user__input']} ${styles['form-user__input--half']}`}
                        field={field}
                        error={errors.ecommerces}
                        isMulti
                        value={formField.ecommerces}
                        isFetching={isLoadingUserDetail}
                        isReadOnly={isReadOnly || isExistingUserHost}
                        isRequired={formField.role?.isHost}
                      />
                    )}
                  />
                </Flex>
              </Flex>

              <Box width={'50%'} paddingRight={'12px'}>
                <InputComponent
                  label={t('formEmailLabel')}
                  placeholder={t('formEmailPlaceholder')}
                  className={styles['form-user__input']}
                  value={formField.email}
                  error={errors.email}
                  isReadOnly={isReadOnly || isExistingUserHost}
                  isRequired={true}
                  isFetching={isLoadingUserDetail}
                  isLoading={isLoadingInlineVerification}
                  register={{
                    ...register('email', {
                      pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email address' },
                      required: 'Email is required',
                    }),
                    onChange: (event) => {
                      onChangeInlineField(event);
                      trigger('email');
                    },
                  }}
                />
              </Box>

              <Box width={'50%'} className={styles['form-user__password-validation']}>
                <InputComponent
                  label={t('formPasswordLabel')}
                  placeholder={isUpsert ? t('formPasswordPlaceholder') : '********'}
                  type="password"
                  className={`${isUpsert ? styles['form-user__input--upsert'] : styles['form-user__input']}`}
                  value={formField.password}
                  error={errors.password}
                  isReadOnly={isReadOnly || isExistingUserHost}
                  isFetching={isLoadingUserDetail}
                  isRequired={isCreateForm}
                  register={{
                    ...register('password', {
                      required: !isEditForm ? 'Password is required' : false,
                    }),
                  }}
                />

                <Condition statement={!isReadOnly && !!formField.password && !validatePassword(formField.password)}>
                  <TextRegular className={styles['form-user__password-validation-error']}>{t('formPasswordInvalid')}</TextRegular>
                </Condition>
                <Condition statement={!isReadOnly && !!formField.password}>
                  <Flex className={styles['form-user__password-validation-item']}>
                    <CheckCircleIcon
                      className={
                        `${styles['form-user__password-validation-icon']} ` +
                        (checkPasswordCriteria(formField.password).minLength ? styles['form-user__password-validation-icon--valid'] : '')
                      }
                    />
                    <TextRegular
                      className={
                        `${styles['form-user__password-validation-text']} ` +
                        (checkPasswordCriteria(formField.password).minLength ? styles['form-user__password-validation-text--valid'] : '')
                      }
                    >
                      {t('formPasswordMinLength')}
                    </TextRegular>
                  </Flex>
                  <Flex className={styles['form-user__password-validation-item']}>
                    <CheckCircleIcon
                      className={
                        `${styles['form-user__password-validation-icon']} ` +
                        (checkPasswordCriteria(formField.password).capitalLetter ? styles['form-user__password-validation-icon--valid'] : '')
                      }
                    />
                    <TextRegular
                      className={
                        `${styles['form-user__password-validation-text']} ` +
                        (checkPasswordCriteria(formField.password).capitalLetter ? styles['form-user__password-validation-text--valid'] : '')
                      }
                    >
                      {t('formPasswordCapitalLetter')}
                    </TextRegular>
                  </Flex>
                  <Flex className={styles['form-user__password-validation-item']}>
                    <CheckCircleIcon
                      className={
                        `${styles['form-user__password-validation-icon']} ` +
                        (checkPasswordCriteria(formField.password).specialChar ? styles['form-user__password-validation-icon--valid'] : '')
                      }
                    />
                    <TextRegular
                      className={
                        `${styles['form-user__password-validation-text']} ` +
                        (checkPasswordCriteria(formField.password).specialChar ? styles['form-user__password-validation-text--valid'] : '')
                      }
                    >
                      {t('formPasswordSpecialCharacter')}
                    </TextRegular>
                  </Flex>
                </Condition>
              </Box>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Condition statement={isCreateForm}>
            <Box className={styles['form-user__button-group']}>
              <ButtonOutline className={styles['form-user__button-batal']} onClick={handleClickCancel} type="button" isDisabled={isSubmitting}>
                <TextSemiBold className={styles['form-user__button-batal-text']}>{t('formCancelButton')}</TextSemiBold>
              </ButtonOutline>
              <ButtonSolid
                className={styles['form-user__button-save']}
                type="submit"
                isLoading={isSubmitting || postUser.isLoading}
                disabled={isSubmitting || postUser.isLoading || !formState.isValid}
              >
                <TextSemiBold className={styles['form-user__button-save-text']}>{t('formSubmitButton')}</TextSemiBold>
              </ButtonSolid>
            </Box>
          </Condition>
        </Form>
      </Box>
    </Box>
  );
};

export default FormUpsert;